import { ImageIcon, UploadCloud, X } from 'lucide-react';
import React, { useRef, useState } from 'react';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import { supabase } from '../../../../lib/supabase';
import { uploadBlogImage } from '../../BlogEditorPage';

interface ImageData {
  url: string;
  alt?: string;
  caption?: string;
}

interface Props {
  data: ImageData;
  onChange: (data: Record<string, unknown>) => void;
}

const BLOG_PUBLISH_API_KEY = import.meta.env.VITE_BLOG_PUBLISH_API_KEY as string | undefined;

export const ImageSectionEditor: React.FC<Props> = ({ data, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const set = (patch: Partial<ImageData>) =>
    onChange({ ...data, ...patch });

  const handleFile = async (file: File) => {
    if (!BLOG_PUBLISH_API_KEY) {
      setUploadError('VITE_BLOG_PUBLISH_API_KEY is not configured.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const bearerToken = session?.access_token ?? '';
      if (!bearerToken) throw new Error('Not authenticated — please sign in again.');
      const url = await uploadBlogImage(file, BLOG_PUBLISH_API_KEY, bearerToken);
      set({ url });
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Upload / URL area */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="relative rounded-xl border-2 border-dashed transition-colors hover:border-[var(--ds-brand)]"
        style={{ borderColor: 'var(--ds-border)' }}
      >
        {data.url ? (
          /* Preview */
          <div className="relative">
            <img
              src={data.url}
              alt={data.alt || 'preview'}
              className="w-full max-h-56 object-cover rounded-xl"
            />
            <button
              type="button"
              onClick={() => set({ url: '' })}
              className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
              title="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          /* Drop zone */
          <div className="flex flex-col items-center justify-center gap-2 py-8 px-4 text-center">
            {uploading ? (
              <LoadingSpinner size="md" showText={false} />
            ) : (
              <UploadCloud className="w-7 h-7" style={{ color: 'var(--ds-text-muted)' }} />
            )}
            <p className="t-meta">
              {uploading ? 'Uploading…' : 'Drag & drop or'}
            </p>
            {!uploading && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                Choose file
              </button>
            )}
            <p className="t-meta mt-1">
              JPEG, PNG, WebP, GIF, AVIF · max 8 MB
            </p>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </div>

      {uploadError && (
        <p className="t-meta t-danger">{uploadError}</p>
      )}

      {/* Manual URL override */}
      <div>
        <label className="t-label block mb-1">
          <ImageIcon className="inline w-3 h-3 mr-1" />
          Or paste image URL
        </label>
        <input
          type="url"
          value={data.url}
          onChange={(e) => set({ url: e.target.value })}
          placeholder="https://example.com/image.jpg"
          className="ds-input"
        />
      </div>

      {/* Alt text */}
      <div>
        <label className="t-label block mb-1">Alt text (accessibility + SEO)</label>
        <input
          type="text"
          value={data.alt ?? ''}
          onChange={(e) => set({ alt: e.target.value })}
          placeholder="Describe the image for screen readers"
          className="ds-input"
        />
      </div>

      {/* Caption */}
      <div>
        <label className="t-label block mb-1">Caption (optional)</label>
        <input
          type="text"
          value={data.caption ?? ''}
          onChange={(e) => set({ caption: e.target.value })}
          placeholder="Short caption shown beneath the image"
          className="ds-input"
        />
      </div>
    </div>
  );
};
